import { getPref, setPref, PREF_KEYS } from './preferences';

/**
 * Accessible view: high-contrast themes, adjustable text size and a simplified layout, plus
 * always-on keyboard / screen reader improvements (modal focus handling, Escape to close,
 * labels for icon-only buttons). Settings are stored per browser.
 */
const KEY = 'a11y';
export const A11Y_THEMES = [
  { id: 'hc-dark', label: 'High contrast: white on black' },
  { id: 'hc-yellow', label: 'High contrast: yellow on black' },
  { id: 'hc-light', label: 'High contrast: black on white' },
];
export const A11Y_DEFAULTS = { enabled: false, theme: 'hc-dark', scale: 130 };
export const A11Y_MIN = 100;
export const A11Y_MAX = 250;

export const loadA11y = () => ({ ...A11Y_DEFAULTS, ...(getPref(KEY, {}) || {}) });

export const applyA11y = (s) => {
  const root = document.documentElement;
  root.setAttribute('data-a11y', s.enabled ? 'on' : 'off');
  root.setAttribute('data-a11y-theme', s.theme);
  root.style.fontSize = s.enabled ? `${s.scale}%` : '';
};

export const saveA11y = (s) => {
  setPref(KEY, s);
  applyA11y(s);
};

// Switching the accessible view on also switches the board to the simpler list view.
export const announceListView = () => {
  setPref(PREF_KEYS.BOARD_VIEW_MODE, 'list');
  window.dispatchEvent(new CustomEvent('mavis-a11y-listview'));
};

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
const visible = (el) => el.offsetParent !== null || el === document.activeElement;

const topOverlay = () => {
  const all = document.querySelectorAll('.modal-overlay');
  return all.length ? all[all.length - 1] : null;
};

const enhanceOverlay = (overlay, opened) => {
  if (overlay.dataset.a11yDone) return;
  overlay.dataset.a11yDone = '1';
  const modal = overlay.querySelector('.modal') || overlay;
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  const heading = overlay.querySelector('h1,h2,h3,.ticket-key');
  overlay.setAttribute('aria-label', (heading && heading.textContent.trim()) || 'Dialog');
  const prev = document.activeElement;
  opened.push({ overlay, prev });
  modal.setAttribute('tabindex', '-1');
  modal.style.outline = 'none';
  // Focus the dialog itself so a screen reader/magnifier starts at its top; Tab then reaches the first control.
  setTimeout(() => { try { modal.focus({ preventScroll: false }); } catch (e) { /* ignore */ } }, 0);
};

const labelIconButtons = (root) => {
  root.querySelectorAll('button:not([aria-label])').forEach((b) => {
    if (!b.textContent.trim() && b.getAttribute('title')) b.setAttribute('aria-label', b.getAttribute('title'));
  });
};

const makeKeyboardClickable = (root) => {
  root.querySelectorAll('tr.list-view-row:not([tabindex])').forEach((r) => r.setAttribute('tabindex', '0'));
  root.querySelectorAll('th[title="Click to sort"]:not([tabindex])').forEach((h) => { h.setAttribute('tabindex', '0'); h.setAttribute('role', 'columnheader'); });
};

export const startA11yEnhancer = () => {
  if (window.__a11yEnhancer) return;
  window.__a11yEnhancer = true;
  const opened = [];
  let queued = false;

  const run = () => {
    queued = false;
    document.querySelectorAll('.modal-overlay').forEach((o) => enhanceOverlay(o, opened));
    // Restore focus to whatever opened a dialog once it closes.
    for (let i = opened.length - 1; i >= 0; i -= 1) {
      if (!opened[i].overlay.isConnected) {
        const { prev } = opened.splice(i, 1)[0];
        if (prev && prev.isConnected) { try { prev.focus(); } catch (e) { /* ignore */ } }
      }
    }
    labelIconButtons(document);
    makeKeyboardClickable(document);
  };
  const schedule = () => { if (!queued) { queued = true; requestAnimationFrame(run); } };
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  schedule();

  document.addEventListener('keydown', (e) => {
    const overlay = topOverlay();
    if (e.key === 'Escape' && overlay) {
      const close = overlay.querySelector('.modal-close');
      e.preventDefault();
      if (close) close.click(); else overlay.click();
      return;
    }
    if (e.key === 'Tab' && overlay) {
      const items = Array.from(overlay.querySelectorAll(FOCUSABLE)).filter(visible);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!overlay.contains(active)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && (active === first || active === overlay.querySelector('.modal'))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
      return;
    }
    if ((e.key === 'Enter' || e.key === ' ') && e.target && e.target.matches && e.target.matches('tr.list-view-row, th[title="Click to sort"]')) {
      e.preventDefault();
      e.target.click();
    }
  });
};

export const initAccessibility = () => {
  applyA11y(loadA11y());
  startA11yEnhancer();
};
